/**
 * UpdateNotification.jsx
 * 
 * Shows a notification banner when a new app version is available.
 * 
 * ISOLATION NOTES:
 * - This component is COMPLETELY SEPARATE from PWA/service worker logic
 * - Does NOT clear caches or manipulate service workers
 * - Simply compares version.json with localStorage and shows a banner
 * - Safe to modify without affecting PWA functionality
 */

import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { RefreshCw, X } from 'lucide-react';
import { topModalCoordinator } from '../lib/topModalCoordinator';

const STORAGE_KEY = 'app-version-seen';
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

export function UpdateNotification() {
  const [showBanner, setShowBanner] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);

  // Register with the top-level modal coordinator while visible.
  useEffect(() => {
    if (showBanner) topModalCoordinator.open('update');
    else topModalCoordinator.close('update');
    return () => topModalCoordinator.close('update');
  }, [showBanner]);

  // When a new version is detected, queue it and wait for the modal slot to
  // be free (with a subtle handoff delay) before showing the banner.
  useEffect(() => {
    if (!pendingVersion) return;
    const cancel = topModalCoordinator.waitUntilFree(() => {
      setNewVersion(pendingVersion);
      setShowBanner(true);
      setPendingVersion(null);
    });
    return cancel;
  }, [pendingVersion]);

  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        // Fetch current version from server (with cache-busting)
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) return;
        
        const serverVersion = await response.json();
        const currentVersion = serverVersion.version;
        
        // Get the version user has already seen/acknowledged
        const seenVersion = localStorage.getItem(STORAGE_KEY);
        
        // If this is a new version the user hasn't seen yet, queue the banner
        // through the top-level modal coordinator so it doesn't stack on top
        // of the install / daily-challenge prompts.
        if (seenVersion && seenVersion !== currentVersion) {
          setPendingVersion(currentVersion);
        }
        
        // Always update the seen version on first load (if not set)
        if (!seenVersion) {
          localStorage.setItem(STORAGE_KEY, currentVersion);
        }
      } catch (error) {
        // Silently fail - update notification is non-critical
        console.debug('Version check failed:', error);
      }
    };

    // Check immediately on mount
    checkForUpdates();

    // Set up periodic checks
    const intervalId = setInterval(checkForUpdates, CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  const handleUpdate = async () => {
    // Store the new version as seen before reloading
    if (newVersion) {
      localStorage.setItem(STORAGE_KEY, newVersion);
    }

    // Force a clean reload so users get fresh assets/data without
    // needing to manually close and reopen the app.
    // 1. Clear all Cache Storage entries (stale HTML/JS/image cache)
    // 2. Tell any waiting Service Worker to activate immediately
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg?.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
    } catch (e) {
      // Non-critical: fall through to reload even if cache clear fails
      console.debug('Update cache-clear failed:', e);
    }

    // Reload to pick up fresh content from the network
    window.location.reload();
  };

  const handleDismiss = () => {
    // Remember this version so we don't show the banner again
    if (newVersion) {
      localStorage.setItem(STORAGE_KEY, newVersion);
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div
      data-testid="update-notification"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div className="relative bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col items-center gap-4 animate-in zoom-in-95 duration-300">
        <RefreshCw className="h-8 w-8 animate-spin-slow" />
        <div className="text-center">
          <div className="text-lg font-semibold">New version available!</div>
          {newVersion && (
            <div className="text-sm opacity-80 mt-1">v{newVersion}</div>
          )}
        </div>
        <div className="flex gap-2 w-full mt-2">
          <Button
            variant="secondary"
            onClick={handleDismiss}
            className="flex-1 bg-white/10 text-white hover:bg-white/20 border border-white/20"
          >
            Later
          </Button>
          <Button
            onClick={handleUpdate}
            className="flex-1 bg-white text-blue-600 hover:bg-blue-50"
          >
            Update Now
          </Button>
        </div>
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default UpdateNotification;
